// NodePercentageField.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './NodePercentageField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Slider from 'components/slider/Slider';

const clampPercentage = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    if (numeric < 0) return 0;
    if (numeric > 100) return 100;
    return Math.round(numeric);
};

const normalizeSnapTo = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return 5;
    return Math.round(parsed);
};

/**
 * NodePercentageField
 *
 * Inline percentage slider. In readOnly / previewMode: display "N%" label only.
 *
 * @param {number|string} value - Current percentage.
 * @param {number} snapTo - Slider snap interval.
 * @param {Function} onChange - Called with snapped percentage.
 * @param {boolean} [readOnly=false] - Display-only label.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 *
 * @returns {ReactNode} The rendered component.
 */
const NodePercentageField = ({
    value,
    snapTo,
    onChange,
    readOnly = false,
    previewMode = false,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const isReadOnly = Boolean(readOnly || previewMode);

    // ----- MEMOS ----- //

    const percentageValue = useMemo(() => clampPercentage(value), [value]);
    const resolvedSnapTo = useMemo(() => normalizeSnapTo(snapTo), [snapTo]);

    // ----- RENDER ----- //

    return (
        <>
            <div className={`node-percentage-field-container${isReadOnly ? ' is-readonly' : ''}`}>

                <div className="node-percentage-field-row">

                    <div className="node-tags-icon">
                        <Icon
                          iconKey="percent"
                          color={theme.foregroundMuted}
                          width={22}
                          height={22}
                        />
                    </div>

                    {isReadOnly ? (
                        <div
                          className="node-percentage-field-value"
                          style={{ color: theme.foreground }}
                        >
                            {`${percentageValue}%`}
                        </div>
                    ) : (
                        <div className="node-tags-icon">
                            <Slider
                              value={percentageValue}
                              min={0}
                              max={100}
                              snapTo={resolvedSnapTo}
                              suffix="%"
                              onChange={onChange}
                            />
                        </div>
                    )}

                </div>

            </div>

        </>
    );
};

NodePercentageField.displayName = 'NodePercentageField';

export default NodePercentageField;
